import { create } from "zustand";
import type { TeamOnboardingStamps, TeamRole } from "ts-interfaces";

// The one-time "Welcome to {team}" modal for people who join a team.

export interface WelcomeTeam {
  firmId: string;
  role: TeamRole;
}

export const teamWelcomeLocalKey = (uid: string, firmId: string) =>
  `teamWelcomeSeen:${uid}:${firmId}`;

export const readWelcomeSeenLocally = (uid: string, firmId: string) => {
  try {
    return !!localStorage.getItem(teamWelcomeLocalKey(uid, firmId));
  } catch {
    return false;
  }
};

/** The team to welcome now: the active workspace's first, then the list order. */
export function pickWelcomeTeam({
  enabled,
  uid,
  settled,
  pathname,
  inviteLinkOpen,
  modalOpen,
  teams,
  activeFirmId,
  stamps,
  localSeen,
  shownThisSession,
}: {
  enabled: boolean;
  uid: string | null;
  /** Profile, permissions and team status loaded (the switcher tip's settle check). */
  settled: boolean;
  pathname: string;
  /** A `?team=` invite link is still being handled (TeamLayout drops it after a join). */
  inviteLinkOpen: boolean;
  /** A subscription or create-team modal is open. */
  modalOpen: boolean;
  /** Active memberships only. */
  teams: WelcomeTeam[];
  activeFirmId: string | null;
  stamps: Record<string, TeamOnboardingStamps> | undefined;
  localSeen: (firmId: string) => boolean;
  /** One welcome per session. */
  shownThisSession: boolean;
}): WelcomeTeam | null {
  if (!enabled || !uid || !settled || modalOpen || shownThisSession)
    return null;
  if (inviteLinkOpen) return null;
  if (!/^\/home(\/|$)/.test(pathname)) return null;
  const fresh = teams.filter(
    (t) => !stamps?.[t.firmId]?.welcomeSeenAt && !localSeen(t.firmId),
  );
  return fresh.find((t) => t.firmId === activeFirmId) ?? fresh[0] ?? null;
}

/** In-session state keyed by uid, so the switcher tip and the welcome video can wait. */
export const useTeamWelcomeStore = create<{
  /** A welcome is about to show or on screen. */
  pendingUid: string | null;
  /** A welcome has shown this session. */
  shownUid: string | null;
  /** Other dialogs on screen (the Library video, the profile form, the invite):
   *  the welcome and the switcher tip wait. */
  blockers: Record<string, boolean>;
  setPending: (uid: string | null) => void;
  markShown: (uid: string) => void;
  setBlocker: (name: string, open: boolean) => void;
}>((set) => ({
  pendingUid: null,
  shownUid: null,
  blockers: {},
  setPending: (uid) => set({ pendingUid: uid }),
  markShown: (uid) => set({ shownUid: uid }),
  setBlocker: (name, open) =>
    set((s) => ({ blockers: { ...s.blockers, [name]: open } })),
}));

/** The team welcome is pending, on screen or already shown this session. */
export const teamWelcomeThisSession = (
  s: { pendingUid: string | null; shownUid: string | null },
  uid: string | null | undefined,
) => !!uid && (s.pendingUid === uid || s.shownUid === uid);
